import request from "../services/HTTPClient";
import { requestMethods, API } from "../constants";
import UserService from "./users";

const getFirstNonEmptyString = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
};

const normalizeSupplierId = (supplierId) => {
  if (supplierId === undefined || supplierId === null) {
    return "";
  }

  return String(supplierId).trim();
};

const extractUserBusinessFromGetByIdResponse = (response) => {
  return getFirstNonEmptyString(
    response?.data?.bussiness,
    response?.data?.user?.bussiness,
    response?.data?.data?.bussiness,
    response?.data?.payload?.bussiness,
    response?.bussiness,
    response?.user?.bussiness,
    response?.payload?.bussiness
  );
};

const getSupplierDisplayName = (product, supplierNamesById = new Map()) => {
  const supplierIdText = normalizeSupplierId(product?.supplier_id);

  const supplierNameFromUsers = supplierIdText
    ? getFirstNonEmptyString(supplierNamesById.get(supplierIdText))
    : "";

  if (supplierNameFromUsers) {
    return supplierNameFromUsers;
  }

  return "Proveedor no especificado";
};

export const getPendingProducts = async () => {
  try {
    const productsResponse = await request({
      url: API.MARKETPLACE.PRODUCTS.GET_ALL,
      method: requestMethods.GET,
    });
    
    let allProducts = [];
    if (productsResponse.data && productsResponse.data.success && Array.isArray(productsResponse.data.products)) {
      allProducts = productsResponse.data.products;
    } else if (productsResponse.data && Array.isArray(productsResponse.data)) {
      allProducts = productsResponse.data;
    } else if (Array.isArray(productsResponse)) {
      allProducts = productsResponse;
    } else if (productsResponse.data && productsResponse.data.data && Array.isArray(productsResponse.data.data)) {
      allProducts = productsResponse.data.data;
    }

    const pendingProducts = allProducts.filter(product => product.archive === true);

    const uniqueSupplierIds = [
      ...new Set(
        pendingProducts
          .map((product) => product?.supplier_id)
          .map((supplierId) => normalizeSupplierId(supplierId))
          .filter((supplierId) => Boolean(supplierId))
      ),
    ];

    const supplierLookupResults = await Promise.allSettled(
      uniqueSupplierIds.map((supplierId) => UserService.GetUserById({ id: supplierId }))
    );

    const supplierNamesById = new Map();
    supplierLookupResults.forEach((result, index) => {
      if (result.status !== "fulfilled") {
        return;
      }

      const supplierId = uniqueSupplierIds[index];
      const supplierName = extractUserBusinessFromGetByIdResponse(result.value);

      if (supplierId && supplierName) {
        supplierNamesById.set(supplierId, supplierName);
      }
    });
    
    const transformedProducts = pendingProducts.map(product => ({
      id: product.id,
      name: product.name,
      sku: product.sku,
      price: product.price,
      imageUrl: product.images && product.images[0] ? product.images[0] : null,
      supplierId:
        product.supplier_id !== undefined && product.supplier_id !== null
          ? normalizeSupplierId(product.supplier_id)
          : "unknown",
      supplierName: getSupplierDisplayName(product, supplierNamesById),
      description: product.description,
      category: product.category,
      stock: product.stock,
      createdAt: product.created_at,
      archive: product.archive
    }));
    
    return { data: transformedProducts, success: true };
  } catch (error) {
    console.error('Error fetching pending products:', error);
    throw error;
  }
};

export const approveProduct = async (productId) => {
  try {
    const currentProductResponse = await request({
      url: `${API.MARKETPLACE.PRODUCTS.GET_BY_ID}${productId}`,
      method: requestMethods.GET,
    });
    
    let currentProduct = currentProductResponse.data;
    if (currentProductResponse.data && currentProductResponse.data.product) {
      currentProduct = currentProductResponse.data.product;
    }
    
    const updateData = {
      ...currentProduct,
      archive: false
    };
    
    return await request({
      url: `${API.MARKETPLACE.PRODUCTS.UPDATE}${productId}`,
      method: requestMethods.PUT,
      data: updateData
    });
  } catch (error) {
    console.error('Error approving product:', error);
    throw error;
  }
};

export const rejectProduct = async (productId, reason) => {
  try {
    return await request({
      url: `${API.MARKETPLACE.PRODUCTS.DELETE}${productId}`,
      method: requestMethods.DELETE,
    });

  } catch (error) {
    console.error('Error rejecting product:', error);
    throw error;
  }
};